import { DashboardLayout } from "components/DashboardLayout";
import { Button } from "components/DesignSystem/Button/Button";
import Dropdown from "components/DesignSystem/Dropdown/Dropdown";
import { Header } from "components/DesignSystem/Header/Header";
import { Export } from "components/icons/Export";
import { ExportOptions } from "components/ExportOptions/ExportOptions";
import { InvoiceFilterType } from "components/InvoiceFilter/InvoiceFilter";
import { BulkInvoiceUpload } from "components/InvoiceList/BulkInvoiceUpload";
import { UploadInvoiceFromPDF } from "components/InvoiceList/UploadInvoiceFromPDF";
import { InvoiceList } from "components/InvoiceList/InvoiceList";
import { Preview } from "components/PreviewModal";
import { ConditionalLink } from "components/conditionalLink";
import { ArrowDown } from "components/icons/ArrowDown";
import { Settings } from "components/icons/Navbar/Settings";
import { INVOICE_ADD_CLICKED } from "constants/analyticsEvents";
import { INVOICE_TYPE_TYPE } from "constants/invoicing";
import { CurrentGroupContextT } from "contexts/CurrentGroupContext";
import { useAnalytics } from "hooks/useAnalytics";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useFilters } from "hooks/useFilter";
import { useModal } from "hooks/useModal";
import { useToast } from "hooks/useToast";
import { parse, stringify } from "qs";
import { ReactNode, useState } from "react";
import {
  Redirect,
  Route,
  Switch,
  useHistory,
  useLocation,
  useRouteMatch,
} from "react-router-dom";
import { RowSelectionState } from "react-table-8.10.7";
import {
  useCreateEntityInvoiceMutation,
  useGetAllInvoicesQuery,
  useGetInvoiceSettingsQuery,
  useLazyGetAllInvoicesQuery,
  usePopulateBulkInvoiceMutation,
  useUploadBulkInvoiceMutation,
  useSuggestTransactionsMutation,
} from "store/apis/invoices";
import { useCreateMessageDraftMutation } from "store/apis/chat";
import { useLazyGetPreviewUrlQuery } from "store/apis/previewUrl";
import { BULK_INVOICE_EXCEL_TEMPLATE_URL } from "types/contants/sampleExcelSheet";
import { openLink } from "utils/openLink";
import { InvoiceFullPageView } from "./InvoiceFullPageView";
import { InvoiceSettingsType } from "types/Models/invoice";
import ConditionalToolTip from "components/design/conditionalToolTip";
import ToolTip from "components/design/toolTip";
import { BOOKS_SETTINGS_TABS } from "constants/booksSettings";
import AIGenerateButtonStar from "static/images/AIGenerateButtonStar.svg";
import UploadSimple from "static/images/UploadSimple.svg";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { RadianButton } from "components/DesignSystem/RadianButton/RadianButton";
import { MagicWand } from "components/icons/MagicWand";
import { useCurrentGroup } from "hooks/useCurrentGroup";
import { colors } from "constants/colors";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { WRITE_PERMISSION_KEYS } from "constants/permissions";
import { LockedButton } from "components/DesignSystem/Button/LockedButton";
import { LockedUi } from "components/LockedUi/LockedUi";

type LayoutProps = {
  children: ReactNode;
  url: string;
  invoiceSetting?: InvoiceSettingsType | null | undefined;
  creatingInvoice: boolean;
  isExporting: boolean;
  isSuggestingTransactions: boolean;
  lastRefreshTime?: string | null;
  onCreateInvoice: () => void;
  openBulkUpload: ReturnType<typeof useModal>;
  openPdfUpload: ReturnType<typeof useModal>;
  onExport: ({ exportToMail }: { exportToMail: boolean }) => void;
  onSuggestTransactions: () => void;
};

const GoToSettingsTooltip = ({
  children,
  url,
  isDisabled,
}: {
  children: React.ReactNode;
  url: string;
  isDisabled: boolean;
}) => (
  <ConditionalToolTip
    side="bottom"
    condition={
      isDisabled && (
        <div className="t-text-left t-w-40">
          <div>Configure invoice settings before using these options.</div>
          <Button customType="link" size="small" color="white">
            <ConditionalLink to={url}>
              <span className="t-text-white t-underline">Go to Settings</span>
            </ConditionalLink>
          </Button>
        </div>
      )
    }
  >
    {children}
  </ConditionalToolTip>
);

const Layout = ({
  children,
  url,
  invoiceSetting,
  creatingInvoice,
  isExporting,
  isSuggestingTransactions,
  lastRefreshTime,
  onCreateInvoice,
  openBulkUpload,
  openPdfUpload,
  onExport,
  onSuggestTransactions,
}: LayoutProps) => {
  const location = useLocation();
  const parsedSearch = parse(location.search, { ignoreQueryPrefix: true });
  const { isAdmin } = useRoleBasedView();
  const { uuid: groupId } = useCurrentGroupContext();
  const currentGroup = useCurrentGroup(groupId);
  const { alertToast } = useToast();

  const history = useHistory();
  const [createUpdateDraft] = useCreateMessageDraftMutation();

  const goToInvoiceAiChat = async (message: string) => {
    const aiChannelId = currentGroup?.ai_channel_url;
    const chatBasePath = isAdmin ? "/admin/chat" : "/books/chat";
    const chatSearch = stringify(
      { entity: parsedSearch.entity, company: parsedSearch.company, ai: true },
      { skipNulls: true, addQueryPrefix: true }
    );
    const chatPath = aiChannelId
      ? `${chatBasePath}/${aiChannelId}${chatSearch}`
      : `${chatBasePath}${chatSearch}`;

    if (aiChannelId) {
      try {
        await createUpdateDraft({ channelUrl: aiChannelId, message }).unwrap();
      } catch (err) {
        alertToast({
          message:
            (err as { data?: { message?: string } })?.data?.message ??
            "Something went wrong",
        });
      }
    }

    history.push(chatPath);
  };

  const handleSmartCreateInvoice = () => {
    void goToInvoiceAiChat("Create an invoice");
  };

  const handleUploadInvoiceFromDocument = () => {
    void goToInvoiceAiChat("Create invoice from uploaded document");
  };

  const search = stringify(
    {
      entity: parsedSearch.entity,
      company: parsedSearch.company,
      tab: BOOKS_SETTINGS_TABS.INVOICES,
    },
    { skipNulls: true, addQueryPrefix: true }
  );

  const settingsUrl = `/books/books-settings${search}`;
  const isDisabled = Boolean(!invoiceSetting);

  dayjs.extend(relativeTime);
  const getRelativeTime = (timestamp?: string | null) => {
    if (!timestamp) return null;
    return dayjs(timestamp).fromNow();
  };

  const relativeRefreshTime = getRelativeTime(lastRefreshTime);

  return (
    <DashboardLayout
      contentProps={{ className: "!t-overflow-hidden" }}
      header={
        <Header
          v2
          title="Invoices"
          right={
            <div className="t-flex t-items-center t-gap-3 t-my-1 sm:t-flex-wrap sm:t-justify-end sm:t-gap-2">
              <ToolTip
                text={
                  <div className="t-text-left t-w-40">
                    <div>Refresh AI suggestions for invoice payments</div>
                    {relativeRefreshTime && (
                      <div className="t-italic t-mt-2 t-text-caption-italic">
                        Last refreshed {relativeRefreshTime}
                      </div>
                    )}
                  </div>
                }
                side="bottom"
              >
                <span>
                  <RadianButton
                    onClick={onSuggestTransactions}
                    className="!t-mt-0"
                    size="small"
                  >
                    {""}
                  </RadianButton>
                </span>
              </ToolTip>
              <Button customType="icon" type="button" size="small">
                <ConditionalLink to={settingsUrl}>
                  <Settings className="t-size-5 t-text-text-30" />
                </ConditionalLink>
              </Button>
              <div className="t-flex t-justify-between t-gap-2">
                <Dropdown.Root>
                  <Dropdown.Trigger asChild className="t-group">
                    <div>
                      <Button
                        size="xsmall"
                        onClick={(e) => e.stopPropagation()}
                        isLoading={isExporting}
                        disabled={isExporting}
                      >
                        <div className="t-flex t-items-center t-gap-1.5">
                          <span className="sm:t-hidden">Export</span>
                          <span className="t-hidden sm:t-flex">
                            <Export />
                          </span>
                          <div className="group-data-state-open:-t-rotate-180 t-transform t-transition t-duration-300 t-ease-in-out">
                            <ArrowDown color="currentColor" />
                          </div>
                        </div>
                      </Button>
                    </div>
                  </Dropdown.Trigger>
                  <Dropdown.Portal>
                    <Dropdown.Content sideOffset={4} align="end" side="bottom">
                      <ExportOptions
                        onLocalDownload={() =>
                          onExport({ exportToMail: false })
                        }
                        onMailExport={() => onExport({ exportToMail: true })}
                      />
                    </Dropdown.Content>
                  </Dropdown.Portal>
                </Dropdown.Root>
                <LockedUi
                  feature={WRITE_PERMISSION_KEYS.REVENUE}
                  blockedUi={
                    <LockedButton text="Create invoice" size="xsmall" />
                  }
                >
                  <Dropdown.Root>
                    <GoToSettingsTooltip
                      url={settingsUrl}
                      isDisabled={isDisabled}
                    >
                      <Dropdown.Trigger
                        asChild
                        className="t-group t-text-text-60"
                      >
                        <div>
                          <Button
                            isLoading={creatingInvoice}
                            disabled={creatingInvoice}
                            size="xsmall"
                            customType="primary"
                            aria-label="Create invoice"
                          >
                            <span className="sm:t-hidden">Create invoice</span>
                            <span className="t-hidden sm:t-inline">Create</span>
                            <div className="group-data-state-open:-t-rotate-180 t-transform t-transition t-duration-300 t-ease-in-out t-text-surface">
                              <ArrowDown
                                color="currentColor"
                                strokeWidth="1.5"
                              />
                            </div>
                          </Button>
                        </div>
                      </Dropdown.Trigger>
                    </GoToSettingsTooltip>
                    <Dropdown.Portal>
                      <Dropdown.Content
                        sideOffset={8}
                        side="bottom"
                        className="t-min-w-48"
                        align="end"
                      >
                        {isAdmin && (
                          <>
                            <Dropdown.Item
                              onSelect={handleSmartCreateInvoice}
                              disabled={isDisabled}
                              className="t-px-3 t-py-2 t-w-full"
                              icon={<MagicWand color={colors.text.black} />}
                            >
                              <div className="t-flex t-items-center t-justify-between t-w-full">
                                <div className="t-flex t-items-center t-gap-2">
                                  <span>Smart create</span>
                                </div>

                                <div className="t-flex t-items-center t-gap-1 t-bg-purple-0 t-text-purple-600 t-text-xs t-px-2 t-rounded-full t-text-body-sm">
                                  <img
                                    src={AIGenerateButtonStar}
                                    alt="AI star"
                                    className="t-w-3 t-h-3"
                                  />
                                  AI
                                </div>
                              </div>
                            </Dropdown.Item>

                            <Dropdown.Item
                              onSelect={handleUploadInvoiceFromDocument}
                              disabled={isDisabled}
                              className="t-px-3 t-py-2 t-w-full"
                              icon={
                                <img
                                  src={UploadSimple}
                                  alt="UploadSimple"
                                  className="t-size-4"
                                />
                              }
                            >
                              <div className="t-flex t-items-center t-justify-between t-w-full">
                                <div className="t-flex t-items-center t-gap-2">
                                  <span>Upload file</span>
                                </div>

                                <div className="t-flex t-items-center t-gap-1 t-bg-purple-0 t-text-purple-600 t-text-xs t-px-2 t-text-body-sm t-rounded-full">
                                  <img
                                    src={AIGenerateButtonStar}
                                    alt="AI star"
                                    className="t-w-3 t-h-3"
                                  />
                                  AI
                                </div>
                              </div>
                            </Dropdown.Item>
                          </>
                        )}
                        <Dropdown.Item
                          onSelect={() => onCreateInvoice()}
                          disabled={isDisabled}
                        >
                          <GoToSettingsTooltip
                            url={settingsUrl}
                            isDisabled={isDisabled}
                          >
                            <div>Create an Invoice</div>
                          </GoToSettingsTooltip>
                        </Dropdown.Item>
                        <Dropdown.Item
                          onSelect={openBulkUpload.open}
                          disabled={isDisabled}
                        >
                          <GoToSettingsTooltip
                            url={settingsUrl}
                            isDisabled={isDisabled}
                          >
                            <div>Import .xls/.xlsx file</div>
                          </GoToSettingsTooltip>
                        </Dropdown.Item>
                        <Dropdown.Item
                          onSelect={openPdfUpload.open}
                          disabled={isDisabled}
                        >
                          <GoToSettingsTooltip
                            url={settingsUrl}
                            isDisabled={isDisabled}
                          >
                            <div>Upload PDF/Image</div>
                          </GoToSettingsTooltip>
                        </Dropdown.Item>
                      </Dropdown.Content>
                    </Dropdown.Portal>
                  </Dropdown.Root>
                </LockedUi>
              </div>
            </div>
          }
        />
      }
    >
      {children}
    </DashboardLayout>
  );
};

export const InvoicesHome = () => {
  const { path, url } = useRouteMatch();
  const location = useLocation();
  const parsedSearch = parse(location.search, { ignoreQueryPrefix: true });
  const goToSettings = useModal();
  const group = useCurrentGroupContext();
  const { trackEvent } = useAnalytics();
  const openBulkUpload = useModal();
  const openPdfUpload = useModal();

  const { values: filter, updateFilter } = useFilters<InvoiceFilterType>({
    initialValue: {
      INVOICE_START_DATE: "",
      INVOICE_END_DATE: "",
      DUEDATE_START_DATE: "",
      DUEDATE_END_DATE: "",
      VOID_START_DATE: "",
      VOID_END_DATE: "",
      UNCOLLECTIBLE_START_DATE: "",
      UNCOLLECTIBLE_END_DATE: "",
      INVOICE_DATE: "",
      DUE_DATE: "",
      SOURCE: [],
      STATUS_GROUP: null,
      STATUS: [],
      CUSTOMER: [],
      CUSTOMER_STATE: [],
      MIN_AMOUNT: "",
      MAX_AMOUNT: "",
      INVOICE_TYPE: "" as INVOICE_TYPE_TYPE,
    },
    useQueryParams: true,
  });

  const [exportInvoices, { isLoading: isExporting }] =
    useLazyGetAllInvoicesQuery();

  const [createInvoice, { isLoading: creatingInvoice }] =
    useCreateEntityInvoiceMutation();

  const [suggestTransactions, { isLoading: isSuggestingTransactions }] =
    useSuggestTransactionsMutation();

  const [getPreviewUrl] = useLazyGetPreviewUrlQuery();
  const entityId = useCurrentEntityId();

  const { data: invoicesData } = useGetAllInvoicesQuery(
    {
      groupId: group?.uuid!,
      entityId: entityId!,
      currentPage: 1,
      searchTerm: null,
      limit: 1,
    },
    { skip: !group?.uuid || !entityId }
  );

  const { successToast, alertToast, info: infoToast } = useToast();

  const onExport = async ({ exportToMail }: { exportToMail: boolean }) => {
    try {
      const exportInvoice = await exportInvoices({
        groupId: group.uuid!,
        entityId: entityId!,
        currentPage: 1,
        searchTerm: null,
        sortCol: undefined,
        sortOrder: undefined,
        startDate: filter.INVOICE_START_DATE,
        endDate: filter.INVOICE_END_DATE,
        dueDateStartDate: filter.DUEDATE_START_DATE,
        dueDateEndDate: filter.DUEDATE_END_DATE,
        markedVoidAtStartDate: filter.VOID_START_DATE,
        markedVoidAtEndDate: filter.VOID_END_DATE,
        markedUncollectibleAtStartDate: filter.UNCOLLECTIBLE_START_DATE,
        markedUncollectibleAtEndDate: filter.UNCOLLECTIBLE_END_DATE,
        customerIds: filter.CUSTOMER.join(","),
        customerStates: filter.CUSTOMER_STATE,
        statuses: filter.STATUS,
        sources: filter.SOURCE,
        minAmount: filter.MIN_AMOUNT,
        maxAmount: filter.MAX_AMOUNT,
        invoiceType: filter.INVOICE_TYPE,
        generate_report: true,
        export_to_email: exportToMail,
      }).unwrap();

      if (exportToMail) {
        return successToast({
          message:
            "Invoices export initiated. You will receive an email shortly",
        });
      }

      if (exportInvoice.excel_document_id) {
        const { download_url } = await getPreviewUrl({
          groupId: group.uuid!,
          fileId: exportInvoice.excel_document_id,
        }).unwrap();

        openLink(download_url);
      }

      successToast({
        message: "Invoices exported successfully",
      });
    } catch (error) {
      alertToast({
        message: "Error exporting invoices",
      });
    }
  };

  const onCreateInvoice = async (skipSettingsCheck: boolean = false) => {
    if (!invoiceSetting && !skipSettingsCheck) {
      goToSettings.open();
      return;
    }

    trackEvent(INVOICE_ADD_CLICKED);

    if (group.uuid && invoiceSetting?.entity_id) {
      try {
        const invoice = await createInvoice({
          groupId: group?.uuid,
          entityId: invoiceSetting?.entity_id,
        }).unwrap();

        history.push(`/books/invoices-and-customers/${invoice.uuid}${search}`);
      } catch (error) {}
    }
  };

  const onSuggestTransactions = async () => {
    if (!group.uuid || !entityId) return;

    try {
      await suggestTransactions({
        groupId: group.uuid,
        entityId,
      }).unwrap();

      infoToast({
        title: "Refreshing",
        message:
          "Updating invoice suggestions. We'll notify you when they are ready",
      });
    } catch (error) {
      alertToast({
        message: "Error refreshing suggested transactions",
      });
    }
  };

  const { data: invoiceSetting, isSuccess: settingsLoaded } =
    useGetInvoiceSettingsQuery(
      {
        groupId: group?.uuid!,
        entityId,
      },
      { skip: !group?.uuid || !entityId }
    );

  const search = stringify(
    {
      entity: parsedSearch.entity,
      company: parsedSearch.company,
    },
    { skipNulls: true, addQueryPrefix: true }
  );

  const booksSettingsSearch = stringify(
    {
      entity: parsedSearch.entity,
      company: parsedSearch.company,
      tab: BOOKS_SETTINGS_TABS.INVOICES,
    },
    { skipNulls: true, addQueryPrefix: true }
  );

  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  const history = useHistory();

  const booksSettingsUrl = `/books/books-settings${booksSettingsSearch}`;

  return (
    <Switch>
      <Route exact path={`${path}/settings`}>
        <Redirect to={booksSettingsUrl} />
      </Route>

      <Route path={`${path}/preview/:previewId/`}>
        <Layout
          url={url}
          invoiceSetting={invoiceSetting}
          creatingInvoice={creatingInvoice}
          isExporting={isExporting}
          isSuggestingTransactions={isSuggestingTransactions}
          lastRefreshTime={
            invoicesData?.last_invoice_transaction_suggestion_run_at
          }
          onCreateInvoice={onCreateInvoice}
          openBulkUpload={openBulkUpload}
          openPdfUpload={openPdfUpload}
          onExport={onExport}
          onSuggestTransactions={onSuggestTransactions}
        >
          <InvoiceList
            goToSettings={goToSettings}
            invoiceSetting={invoiceSetting}
            group={group as CurrentGroupContextT}
            filter={filter}
            updateFilter={updateFilter}
            rowSelection={rowSelection}
            setRowSelection={setRowSelection}
          />
          <Preview
            closeModal={() => history.push(`${url}${search}`)}
            groupId={group.uuid}
            showModal={true}
          />
          {openBulkUpload.isOpen && (
            <BulkInvoiceUpload
              {...openBulkUpload}
              usePopulateMutation={usePopulateBulkInvoiceMutation}
              useUploadMutation={useUploadBulkInvoiceMutation}
              templateUrl={BULK_INVOICE_EXCEL_TEMPLATE_URL}
            />
          )}
          {openPdfUpload.isOpen && <UploadInvoiceFromPDF {...openPdfUpload} />}
        </Layout>
      </Route>

      <Route path={`${path}/:invoiceId`}>
        <InvoiceFullPageView />
      </Route>

      <Route path={path}>
        <Layout
          url={url}
          invoiceSetting={invoiceSetting}
          creatingInvoice={creatingInvoice}
          isExporting={isExporting}
          isSuggestingTransactions={isSuggestingTransactions}
          lastRefreshTime={
            invoicesData?.last_invoice_transaction_suggestion_run_at
          }
          onCreateInvoice={onCreateInvoice}
          openBulkUpload={openBulkUpload}
          openPdfUpload={openPdfUpload}
          onExport={onExport}
          onSuggestTransactions={onSuggestTransactions}
        >
          <InvoiceList
            invoiceSetting={invoiceSetting}
            goToSettings={goToSettings}
            group={group as CurrentGroupContextT}
            filter={filter}
            updateFilter={updateFilter}
            rowSelection={rowSelection}
            setRowSelection={setRowSelection}
          />
          {openBulkUpload.isOpen && (
            <BulkInvoiceUpload
              {...openBulkUpload}
              usePopulateMutation={usePopulateBulkInvoiceMutation}
              useUploadMutation={useUploadBulkInvoiceMutation}
              templateUrl={BULK_INVOICE_EXCEL_TEMPLATE_URL}
            />
          )}
          {openPdfUpload.isOpen && <UploadInvoiceFromPDF {...openPdfUpload} />}
        </Layout>
      </Route>
    </Switch>
  );
};
